import { getBaseWidth, getBaseDepth, getBaseHeight } from './dimension';

/**
 * Checks if the given string is a valid hex color.
 *
 * A valid hex color is a string that is either 3 or 6 characters long, and consists
 * only of the characters 0-9, A-F, and a-f. The string must also start with a '#'
 * character.
 *
 * @param {string} hex The string to check.
 * @returns {boolean} True if the string is a valid hex color, false otherwise.
 */
export const isValidHexColor = hex => {
    const regex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return regex.test( hex );
}

/**
 * Validates whether a given string is a valid email address.
 * @param {string} email The string to validate as an email address.
 * @returns {boolean} True if the string is a valid email address, false otherwise.
 */
export const isValidateEmail = email => {
    return String( email )
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
}

/**
 * Checks whether the given condition object is valid for upgrades.
 * @param {Object} cond The condition object to check.
 * @returns {boolean} True if the condition is valid, false otherwise.
 */
export const isValidConditionUpgrades = cond => {
    const sizes = [
        { size: getBaseWidth(), operator: cond.width_condition.operator, value: cond.width_condition.value, max: cond.width_condition.max },
        { size: getBaseDepth(), operator: cond.length_condition.operator, value: cond.length_condition.value, max: cond.length_condition.max },
        { size: getBaseHeight(), operator: cond.height_condition.operator, value: cond.height_condition.value, max: cond.height_condition.max }
    ];

    for( let i = 0; i < sizes.length; i++ ) {
        if( ! isValidSize( sizes[i]['operator'], sizes[i]['size'], sizes[i]['value'], sizes[i]['max'] ) ) {
            return false;
        }
    }

    return true;
}

/**
 * Checks whether a given size is valid based on the provided operator, min, and max.
 * @param {string} operator - The operator to use for the size check.
 * @param {number|string} size - The size to check.
 * @param {number|string} min - The minimum value for the size check.
 * @param {number|string} [max=0] - The maximum value for the size check.
 * @returns {boolean} True if the size is valid, false otherwise.
 */
export const isValidSize = ( operator, size, min, max = 0 ) => {
    if( false === min || '' === min || false === operator || '' === operator ) {
        return true;
    }

    const minNumber     = Number( min );
    const sizeNumber    = Number( size );

    switch( operator ) {
        case 'equal':
            return sizeNumber === minNumber;
        case 'greater':
            return sizeNumber > minNumber;
        case 'greater_or_equal':
            return sizeNumber >= minNumber;
        case 'less':
            return sizeNumber < minNumber;
        case 'less_or_equal':
            return sizeNumber <= minNumber;
        case 'between':
            return sizeNumber >= minNumber && sizeNumber <= Number( max );
    }

    return false;
}

/**
 * Checks whether a given material is valid based on the provided type and data.
 * @param {string} type - The type of material to check. Can be 'siding', 'roof', or 'floor'.
 * @param {string[]} data - The list of valid materials for the given type.
 * @returns {boolean} True if the material is valid, false otherwise.
 */
export const isValidMaterial = ( type, data ) => {
    if( ! data || 0 === data.length ) return true;
    let currentMaterial = '';
    switch( type ) {
        case 'siding': 
            currentMaterial = jQuery( `.ssb-configurator-siding-type li.active` ).data( 'siding-type' );
            break;
        case 'roof': 
            currentMaterial = jQuery( `.ssb-configurator-roof-type li.active` ).data( 'roof-type' );
            break;
        case 'floor': 
            currentMaterial = jQuery( `.ssb-configurator-floor-type li.active` ).data( 'floor-type' );
            break;
    }
    if( '' === currentMaterial ) return true;

    return data.includes( currentMaterial );
}

/**
 * Checks if the given string is a valid Base64-encoded string.
 *
 * @param {string} base64Data - The string to check.
 * @returns {boolean} True if the string is a valid Base64-encoded string, false otherwise.
 */
export const isValidBase64 = ( base64Data ) => {
    if( undefined === base64Data || '' === base64Data || '' === base64Data.trim() ) {
        return false;
    }

    try {
        JSON.parse( window.atob( base64Data ) );

        return window.btoa( window.atob( base64Data ) ) === base64Data;
    } catch ( err ) {
        return false;
    }
}